import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, map, Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { AuthenticatedUser, Permission } from '../models';

interface ApiResponse<T> {
  succeeded: boolean;
  data: T;
}

interface UserProfileDto {
  id?: number | string;
  firstName?: string;
  lastName?: string;
  role?: string;
  email?: string;
  phone?: string;
  company?: string;
  languagePreferance?: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserContextService {
  constructor(private readonly http: HttpClient) {}

  updateLanguagePreference(userId: string | number, language: string): Observable<boolean> {
    return this.http.put<boolean>(
      `${environment.apiBaseUrl}/users/UpdateLanguagePreference?userId=${userId}&language=${language}`,
      {}
    );
  }

  async loadUserAndPermissions(): Promise<{ user: AuthenticatedUser; permissions: Permission[] } | null> {
    try {
      const userRequest = this.http.get<ApiResponse<UserProfileDto>>(`${environment.apiBaseUrl}/users/me`);
      const permissionsRequest = this.http.get<ApiResponse<Permission[]>>(`${environment.apiBaseUrl}/permissions`);

      const [userResponse, permissionResponse] = await Promise.all([
        firstValueFrom(userRequest),
        firstValueFrom(permissionsRequest)
      ]);

      if (!userResponse?.succeeded || !userResponse.data) {
        return null;
      }

      return {
        user: this.mapUser(userResponse.data),
        permissions: this.mapPermissions(permissionResponse?.data)
      };
    } catch {
      return null;
    }
  }

  private mapUser(dto: UserProfileDto): AuthenticatedUser {
    const displayName = `${dto.firstName ?? ''} ${dto.lastName ?? ''}`.trim();
    const role = dto.role ?? null;

    return {
      userId: String(dto.id ?? ''),
      displayName: displayName || 'Unknown User',
      role,
      roles: role ? [role] : [],
      email: dto.email ?? null,
      phoneNumber: dto.phone ?? null,
      preferredLanguage: dto.languagePreferance ?? 'en-ca',
      company: dto.company?.trim() || null
    };
  }

  private mapPermissions(input: Permission[] | undefined): Permission[] {
    if (!Array.isArray(input)) {
      return [];
    }

    return input
      .filter((item) => typeof item?.slug === 'string' && item.slug.length > 0)
      .map((item) => ({ slug: item.slug, name: item.name }));
  }
}